import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import HostPayoutNavbar from '../../components/host/HostPayoutNavbar';
import HostPayoutStatusCard from '../../components/host/HostPayoutStatusCard.jsx';
import { LuChevronDown } from "react-icons/lu";




export default function HostPayouts() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [payoutStatus, setPayoutStatus] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [stripeLoginUrl, setStripeLoginUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('desc');

  // ✅ Pagination
  const PAGE_SIZE = 9;
  const [page, setPage] = useState(1);

  // -----------------------------
  // GATE NON-HOSTS
  // -----------------------------
  useEffect(() => {
    if (!currentUser?.isHost) {
      navigate('/events', { replace: true });
    }
  }, [currentUser, navigate]);

  // -----------------------------
  // FETCH DATA
  // -----------------------------
  useEffect(() => {
  if (!currentUser?.isHost) return;

  async function fetchData() {
    try {
      const token =
        localStorage.getItem('accessToken') ||
        localStorage.getItem('authToken') ||
        '';

      const [statusRes, historyRes, stripeRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/host/payouts/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/host/payouts/history`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/host/stripe/login`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      // ✅ status
      setPayoutStatus(statusRes.data);

      // ✅ payouts (REAL DATA ONLY)
      const realPayouts = historyRes.data?.payouts || [];
      setPayouts(realPayouts);

      // ✅ stripe login
      setStripeLoginUrl(stripeRes.data?.url || null);
    } catch (err) {
      console.error('[HostPayouts] fetch failed', err);
    } finally {
      setLoading(false);
    }
  }

  fetchData();
}, [currentUser]);

  // -----------------------------
  // HELPERS
  // -----------------------------
  function formatMoney(n) {
    return `$${Number(n || 0).toFixed(2)}`;
  }

  function monthLabel(period) {
    const [y, m] = period.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }

  function paidOnLabel(paidAt) {
    if (!paidAt) return '—';
    return new Date(paidAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  // -----------------------------
  // SORT + PAGINATE
  // -----------------------------
  const sortedPayouts = [...payouts].sort((a, b) =>
    sortOrder === 'asc'
      ? a.payoutPeriod.localeCompare(b.payoutPeriod)
      : b.payoutPeriod.localeCompare(a.payoutPeriod)
  );

  const totalPages = Math.max(
    1,
    Math.ceil(sortedPayouts.length / PAGE_SIZE)
  );

  const paginatedPayouts = sortedPayouts.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Reset page on sort change
  useEffect(() => {
    setPage(1);
  }, [sortOrder]);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  async function startPayoutSetup() {
    try {
      const token =
        localStorage.getItem('accessToken') ||
        localStorage.getItem('authToken') ||
        '';

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/host/payouts/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('[HostPayouts] setup failed', err);
      alert('Failed to start payout setup.');
    }
  }

  if (!currentUser?.isHost) return null;

  // -----------------------------
  // Custom dropdown (unchanged)
  // -----------------------------
  function SortDropdown({ value, onChange, options }) {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
      const onClick = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const selectedLabel =
      options.find((o) => o.value === value)?.label || '';

    return (
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={ui.sortTrigger}
        >
<span style={{ fontWeight: 600, color: '#0b0b0b', WebkitTextFillColor: '#0b0b0b' }}>
  {selectedLabel}
</span>
<LuChevronDown
  aria-hidden
  style={{
    fontSize: 16,
    color: "#9ca3af",
    lineHeight: 0,
    flexShrink: 0,
  }}
/>
        </button>

        {open && (
          <ul style={ui.dropdown}>
            {options.map((opt) => (
              <li
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  ...ui.dropdownItem,
                  background:
                    opt.value === value ? '#f3f4f6' : '#fff',
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div style={ui.page}>
      <div style={ui.container}>
        <HostPayoutNavbar
          title="Payouts"
          backTo="/host"
          payoutStatus={payoutStatus}
        />

        <HostPayoutStatusCard
          payoutStatus={payoutStatus}
          stripeLoginUrl={stripeLoginUrl}
          onSetupStripe={startPayoutSetup}
        />

        {/* TABLE CONTROLS */}
        <div style={ui.tableHeaderRow}>
          <div style={ui.tableTitle}>Payout history</div>

          <SortDropdown
            value={sortOrder}
            onChange={setSortOrder}
            options={[
              { value: 'desc', label: 'Newest first' },
              { value: 'asc', label: 'Oldest first' },
            ]}
          />
        </div>

        {/* TABLE */}
        <div style={ui.table}>
          
          <div style={ui.tableHead}>
            <div>Date</div>
            <div>Amount</div>
            <div>Status</div>
            <div>Paid on</div>
          </div>

          {loading ? (
            <div style={ui.empty}>Loading payouts…</div>
          ) : paginatedPayouts.length === 0 ? (
            <div style={ui.empty}>No payouts yet.</div>
          ) : (
            paginatedPayouts.map((p) => (
              <div key={p._id} style={ui.tableRow}>
                <div>{monthLabel(p.payoutPeriod)}</div>
                <div style={ui.amount}>{formatMoney(p.amount)}</div>
                <div style={ui.paidPill}>Paid</div>
                <div>{paidOnLabel(p.paidAt)}</div>
              </div>
            ))
          )}
        </div>

        {/* PAGINATION */}
       {totalPages > 1 && (
  <div style={ui.pagination}>
    {/* Previous */}
    <button
  onClick={() => {
  if (page > 1) {
    setPage(page - 1);
  }
}}

      disabled={page === 1}
      style={{
        ...ui.pageBtn,
        ...(page === 1 ? ui.pageBtnDisabled : {}),
      }}
    >
      ‹
    </button>

    {/* Page numbers */}
    {Array.from({ length: totalPages }).map((_, i) => {
      const pageNum = i + 1;
      const active = pageNum === page;

      return (
        <button
          key={pageNum}
          onClick={() => {
  setPage(pageNum);
}}

          style={{
            ...ui.pageBtn,
            ...(active ? ui.pageBtnActive : {}),
          }}
        >
          {pageNum}
        </button>
      );
    })}

    {/* Next */}
    <button
     onClick={() => {
  if (page < totalPages) {
    setPage(page + 1);
  }
}}

      disabled={page === totalPages}
      style={{
        ...ui.pageBtn,
        ...(page === totalPages ? ui.pageBtnDisabled : {}),
      }}
    >
      ›
    </button>
  </div>
)}
      </div>
    </div>
  );
}

/* ===============================
   ADMIN-STYLE UI
================================ */

const ui = {
  page: {
    minHeight: '100vh',
    background: '#fff',
  },

  container: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '8px 16px 16px',
  },

  tableHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    marginTop: 8,
  },

  tableTitle: {
    fontSize: 14,
    fontWeight: 900,
  },

 sortTrigger: {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '8px 12px',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 700,
  display: 'flex',
  gap: 8,
  alignItems: 'center',

  // ✅ force NOT system-blue on iOS
  color: '#0b0b0b',
  WebkitTextFillColor: '#0b0b0b',
  appearance: 'none',
  WebkitAppearance: 'none',
  WebkitTapHighlightColor: 'transparent',
  textDecoration: 'none',
},


dropdown: {
  position: 'absolute',
  top: '100%',
  right: 0,
  zIndex: 50,
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  listStyle: 'none',
  padding: 0,
  marginTop: 6,
  minWidth: 160,

  // ✅ prevent system blue in the menu
  color: '#0b0b0b',
  WebkitTextFillColor: '#0b0b0b',
},


  dropdownItem: {
  padding: '10px 12px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,

  // ✅ force text color
  color: '#0b0b0b',
  WebkitTextFillColor: '#0b0b0b',
  textDecoration: 'none',
},

  table: {
    borderTop: '1px solid #E5E7EB',
  },

  tableHead: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    padding: '10px 0',
    fontSize: 12,
    fontWeight: 800,
    color: '#6B7280',
  },

  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    padding: '12px 0',
    borderTop: '1px solid #E5E7EB',
    fontSize: 14,
    alignItems: 'center',
  },

  amount: {
    fontWeight: 900,
  },

  paidPill: {
    fontSize: 12,
    fontWeight: 700,
    color: '#166534',
    background: '#E6F4EA',
    padding: '4px 10px',
    borderRadius: 999,
    width: 'fit-content',
  },

  empty: {
    padding: '24px 0',
    fontSize: 13,
    color: '#777',
  },

  pagination: {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 6,
  marginTop: 16,
},

pageBtn: {
  minWidth: 32,
  height: 32,
  padding: '0 8px',

  borderRadius: 6,
  border: '1px solid #E5E7EB',   // 👈 consistent hairline
  background: '#fff',

  fontSize: 12,
  fontWeight: 700,
  color: '#0b0b0b',

  cursor: 'pointer',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  outline: 'none',               // 👈 prevents thick focus ring
},

pageBtnActive: {
  background: '#000',         // 👈 subtle active
  borderColor: '#000',
  color: '#fff',
},

pageBtnDisabled: {
  color: '#9CA3AF',
  cursor: 'not-allowed',
  background: '#fff',
  borderColor: '#E5E7EB',
},


};
